// Partager un graphique par lien (CDC E2, décidé par Marc le 03/10/2026) — le contrat avec le
// serveur (`app/observatoire/partages.py`).
//
// Le navigateur n'envoie QUE l'identifiant du graphique (`dem-1.population-depuis-1876`) : le
// serveur reconstruit lui-même le graphique avec les droits de la personne qui partage, et
// fabrique l'instantané figé que le lien montrera pendant 14 jours. Aucune donnée de graphique
// ne part d'ici (CDC § 3, E2-A2).
//
// Côté visiteur, l'instantané arrive DANS la page (`<script id="terobs-partage">`, posé par le
// serveur qui sert `/p/<jeton>`) : la page n'a rien à demander. L'appel à `/api/partages/<jeton>`
// n'est qu'un repli (navigation interne, serveur de développement).
import axios from 'axios'

import { API } from '../types'
import type { ChartSpec } from '../../observatoire/ChartBlock'
import type { Fiche } from '../FicheTechnique'

export interface InstantanePartage {
  version: number
  commune: { code: string; nom: string }
  thematique: { id: string; libelle: string; couleur?: string | null }
  section: { id: string; titre?: string | null }
  graphique: { id: string; titre: string; unite?: string | null; note?: string | null; spec: ChartSpec }
  provenance: { sources: { nom: string; cadence?: string }[]; donnees_au: string; fiche: Fiche | null }
  duree: { cree_le: string; expire_le: string }
  origine: 'commune' | 'terobs'
  page_publique: string
  // A20 (R1, R7) : l'instantané garde la thématique et la page du jour du partage. Le
  // serveur ajoute où la section vit AUJOURD'HUI dans le registre actif — `log-14`, partagée
  // sous `log`, est servie sous `urb`. Les deux formes sont lues (identifiant ou objet, page
  // en chemin ou en identifiant) ; absents, l'instantané fait foi. Voir `thematiqueActuelle`.
  thematique_actuelle?: string | { id: string; libelle?: string | null; couleur?: string | null } | null
  page_actuelle?: string | null
}

/** La thématique à AFFICHER pour un partage : l'actuelle si le serveur la sert avec son
    libellé, sinon celle de l'instantané (un identifiant seul ne dit pas le libellé). */
export function thematiqueActuelle(inst: InstantanePartage): InstantanePartage['thematique'] {
  const a = inst.thematique_actuelle
  if (a && typeof a === 'object' && a.id && a.libelle) {
    return { id: a.id, libelle: a.libelle, couleur: a.couleur ?? inst.thematique.couleur }
  }
  return inst.thematique
}

/** La page qui montre la section AUJOURD'HUI (A20) : `page_actuelle` (chemin, ou identifiant de
    thématique), puis `thematique_actuelle`, puis la page de l'instantané. Sans ancre. */
export function pageActuelle(inst: Pick<InstantanePartage, 'commune' | 'page_publique'
  | 'page_actuelle' | 'thematique_actuelle'>): string {
  const p = inst.page_actuelle
  if (typeof p === 'string' && p.startsWith('/')) return p
  const a = inst.thematique_actuelle
  const id = (typeof p === 'string' && p && p !== 'synthese' ? p : null)
    ?? (typeof a === 'string' ? a : a?.id) ?? null
  return id && inst.commune?.code ? `/o/${inst.commune.code}/t/${id}` : inst.page_publique
}

/** A20 (lot B, `partages._ici`) : où la section du lien vit AUJOURD'HUI, servi À CÔTÉ de
    l'instantané figé (jamais dedans). `page_publique` est alors l'adresse de la page qui porte
    la section ; `section_actuelle` la section à ouvrir (elle-même, ou sa remplaçante :
    `soc-1` → `rev-1`). Tous absents d'un serveur antérieur. */
export interface IciPartage {
  page_publique?: string | null
  page_actuelle?: string | null
  thematique_actuelle?: string | null
  section_actuelle?: string | null
  libelle_thematique_actuelle?: string | null
}

export type ChargePartage =
  | ({ etat: 'valide'; instantane: InstantanePartage } & IciPartage)
  | ({ etat: 'expire'; commune: { code: string; nom: string | null } } & IciPartage & { page_publique: string })
  | { etat: 'inconnu' }

/** Le lien « voir la page » d'un partage valide : page et section d'AUJOURD'HUI servies par la
    charge, l'instantané en repli. */
export function lienPagePartage(inst: InstantanePartage, ici: IciPartage): string {
  const page = ici.page_publique || pageActuelle(inst)
  return `${page}#${ici.section_actuelle || inst.section.id}`
}

export interface LienCree {
  id: number; url: string; titre: string; commune: string
  origine: 'commune' | 'terobs'; cree_le: string; expire_le: string
  /** Vrai si l'adresse pourra être réaffichée dans « Mes partages » (migration 0087 en place).
      Absent ou faux : la fenêtre demande de copier le lien tout de suite. */
  recopiable?: boolean
}

/** Les appels à l'action comptés (§ 9) — liste close, la même que le serveur. */
export type CibleClic = 'votre_commune' | 'page_publique' | 'creer_compte' | 'demo'

export const ID_CHARGE_INJECTEE = 'terobs-partage'

/** L'instantané posé dans la page par le serveur — `null` s'il n'y est pas (ou illisible). */
export function lireChargeInjectee(): ChargePartage | null {
  const el = typeof document !== 'undefined' ? document.getElementById(ID_CHARGE_INJECTEE) : null
  if (!el?.textContent) return null
  try {
    const charge = JSON.parse(el.textContent) as ChargePartage
    return charge && typeof charge === 'object' && 'etat' in charge ? charge : null
  } catch {
    return null
  }
}

/** Repli : l'instantané demandé au serveur. 410 et 404 portent eux aussi leur charge. */
export async function chargerPartage(jeton: string): Promise<ChargePartage> {
  try {
    const r = await axios.get<ChargePartage>(`${API}/partages/${encodeURIComponent(jeton)}`)
    return r.data
  } catch (e) {
    const data = axios.isAxiosError(e) ? e.response?.data as ChargePartage | undefined : undefined
    if (data && typeof data === 'object' && 'etat' in data) return data
    throw e
  }
}

/** Le signal d'un appel à l'action — sans identifiant, sans cookie, et sans jamais bloquer le
    geste (`keepalive` : la page peut se fermer, le signal part quand même). */
export function signalerClic(jeton: string, cible: CibleClic): void {
  try {
    void fetch(`${API}/partages/${encodeURIComponent(jeton)}/clic`, {
      method: 'POST', keepalive: true, credentials: 'omit',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cible }),
    }).catch(() => { /* un compteur ne casse jamais un geste */ })
  } catch { /* fetch absent : rien à compter */ }
}

/** Crée le lien d'un graphique. Le message du serveur (quota, thématique, coupure) remonte tel
    quel : il est écrit pour l'élu. */
export async function creerLien(insee: string, graphique: string, token: string | null): Promise<LienCree> {
  try {
    const r = await axios.post<LienCree>(`${API}/observatoire/${insee}/partages`, { graphique },
      { headers: token ? { Authorization: `Bearer ${token}` } : undefined })
    return r.data
  } catch (e) {
    const detail = axios.isAxiosError(e) ? (e.response?.data as { detail?: unknown } | undefined)?.detail : undefined
    throw new Error(typeof detail === 'string' ? detail : 'Le lien n’a pas pu être créé. Réessayez dans un instant.')
  }
}

/** « Recréer » (05/10) : un lien d'avant la recopie des adresses n'a pas d'adresse à réafficher.
    Le serveur en fabrique un neuf pour le même graphique et retire l'ancien, en une transaction.
    Mêmes messages que la création (quota, coupure, thématique). */
export async function recreerLien(insee: string, id: number, token: string | null): Promise<LienCree> {
  try {
    const r = await axios.post<LienCree>(`${API}/observatoire/${insee}/partages/${id}/recreer`, {},
      { headers: token ? { Authorization: `Bearer ${token}` } : undefined })
    return r.data
  } catch (e) {
    const detail = axios.isAxiosError(e) ? (e.response?.data as { detail?: unknown } | undefined)?.detail : undefined
    throw new Error(typeof detail === 'string' ? detail : 'Le lien n’a pas pu être recréé. Réessayez dans un instant.')
  }
}

/** « 17/10/2026 », lu à Paris. */
export function dateCourte(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric' })
}
